import { InjectionToken, WritableSignal } from '@angular/core';

/**
 * Section Renderer Models
 * Common interfaces for full-width section rendering.
 */

/**
 * Layout variant types per section
 */
export type HeroLayoutVariant = 'centered-background' | 'angled-right' | 'simple';
export type HeaderLayoutVariant = 'simple' | 'transparent';
export type FooterLayoutVariant = 'simple-centered' | 'with-social' | 'multi-column' | 'minimal';
export type TeamLayoutVariant = 'grid' | 'large-images' | 'small-avatars' | 'medium-cards';
export type StatsLayoutVariant = 'simple' | 'cards';
export type FeaturesLayoutVariant = 'grid' | 'alternating' | 'icon-list';
export type CTALayoutVariant = 'simple' | 'split' | 'with-image';
export type BlogLayoutVariant = 'grid' | 'single-col' | 'featured';

/**
 * Background configuration for sections
 */
export type BackgroundType = 'inherit' | 'solid' | 'gradient' | 'image' | 'transparent';

export interface SectionBackground {
  type: BackgroundType;
  color?: string;
  gradient?: string;
  imageUrl?: string;
  overlay?: number; // 0-100 opacity for dark overlay on images
}

/**
 * Base section settings that all sections share
 */
export interface BaseSectionSettings {
  layoutVariant?: string;
  backgroundType?: BackgroundType;
  backgroundColor?: string;
  backgroundGradient?: string;
  backgroundImageId?: string;
  backgroundImageUrl?: string;
  backgroundOverlay?: number;
}

/**
 * CTA item for buttons and links in sections
 */
export interface CtaItem {
  /** Unique identifier for this CTA */
  id?: string;
  /** Whether this CTA item is enabled/visible */
  enabled?: boolean;
  /** Display style: 'button' for styled button, 'link' for text link */
  style: 'button' | 'link';
  /** Link type: 'external' for URL, 'scroll' for scroll-to-section */
  linkType: 'external' | 'scroll';
  /** For external links: the URL */
  url?: string;
  /** For scroll links: the section ID to scroll to */
  targetSectionId?: string;
  /** For scroll links: the section anchor (slugified name) for the href */
  targetSectionAnchor?: string;
  /** Button/link text in German */
  textDe?: string;
  /** Button/link text in English */
  textEn?: string;
  /** @deprecated Legacy single text field - use textDe/textEn instead */
  text?: string;
  /** Runtime flag indicating this item uses placeholder text (for styling) */
  isPlaceholder?: boolean;
}

/**
 * Hero section settings (shared across languages)
 */
export interface HeroSettings extends BaseSectionSettings {
  layoutVariant?: HeroLayoutVariant;
  showAnnouncement?: boolean;
  showCta?: boolean;
  ctaStyle?: 'primary' | 'secondary';
  /** Percentage of screen width for image area in angled-right variant (30-70) */
  angleSplit?: number;
  /** CTA items with configuration and language-specific text */
  ctaItems?: CtaItem[];
}

/**
 * Hero section content
 */
export interface HeroContent {
  title?: string;
  subtitle?: string;
  announcementText?: string;
  announcementLink?: string;
  /** New CTA items array format */
  ctaItems?: CtaItem[];
  // Legacy fields (for backward compatibility)
  ctaText?: string;
  ctaUrl?: string;
  secondaryCtaText?: string;
  secondaryCtaUrl?: string;
  imageUrl?: string;
}

/**
 * Header section settings
 */
export interface HeaderSettings extends BaseSectionSettings {
  layoutVariant?: HeaderLayoutVariant;
  showNavLinks?: boolean;
  showCtaButton?: boolean;
  sticky?: boolean;
  transparent?: boolean;
  /** Navigation links - stored in settings for structure (same pattern as Footer) */
  navLinks?: NavLink[];
  /** Logo image media ID (resolved to logoUrl at runtime) */
  logoImageId?: string;
  /** Resolved logo URL (populated from logoImageId) */
  logoUrl?: string;
}

/**
 * Header section content
 */
export interface HeaderContent {
  logoUrl?: string;
  logoAlt?: string;
  logoText?: string;
  navLinks?: NavLink[];
  ctaText?: string;
  ctaUrl?: string;
}

/** Link type for navigation items */
export type NavLinkType = 'url' | 'scroll' | 'legal';

export interface NavLink {
  /** Link type: 'url' for external/internal URL, 'scroll' for scroll-to-section, 'legal' for Imprint/Privacy */
  linkType?: NavLinkType;
  /** For legal links, identifies which legal page - label/url resolved from PageSettings */
  legalKey?: 'imprint' | 'privacy';
  /** German label */
  labelDe?: string;
  /** English label */
  labelEn?: string;
  /** For URL links: the URL to navigate to */
  url?: string;
  /** For scroll links: section ID to scroll to */
  targetSectionId?: string;
  /** For scroll links: pre-computed anchor name (slugified section name) */
  targetSectionAnchor?: string;

  // Legacy fields (for backwards compatibility during migration)
  /** @deprecated Use linkType instead */
  type?: 'legal' | 'custom';
  /** @deprecated Use labelDe/labelEn instead */
  label?: string;
}

/**
 * Footer section settings
 */
export interface FooterSettings extends BaseSectionSettings {
  layoutVariant?: FooterLayoutVariant;
  showNavLinks?: boolean;
  showSocialLinks?: boolean;
  showCopyright?: boolean;
  /** Company name (shared across languages) */
  companyName?: string;
  /** Tagline/slogan (shared across languages) */
  tagline?: string;
  /** Copyright year (defaults to current year) */
  copyrightYear?: number;
  /** Custom copyright text (overrides auto-generated) */
  copyrightText?: string;
  /** Social links (shared across languages, no translation needed) */
  socialLinks?: SocialLink[];
}

/**
 * Footer section content
 */
export interface FooterContent {
  navLinks?: NavLink[];
  socialLinks?: SocialLink[];
  copyrightText?: string;
  companyName?: string;
  tagline?: string;
}

export interface SocialLink {
  platform: 'facebook' | 'instagram' | 'twitter' | 'linkedin' | 'youtube' | 'github';
  url: string;
}

/**
 * Team member settings (shared structure across languages)
 */
export interface TeamMemberSettings {
  /** Unique identifier for syncing with content bios */
  id: string;
  /** Member name */
  name: string;
  /** Member role/position */
  role: string;
  /** Image media ID (optional) */
  imageId?: string | null;
  /** Email address for mailto link (optional) */
  email?: string;
}

/**
 * Team section settings
 */
export interface TeamSettings extends BaseSectionSettings {
  layoutVariant?: TeamLayoutVariant;
  showRole?: boolean;
  showBio?: boolean;
  showEmail?: boolean;
  showLocation?: boolean;
  showSocialLinks?: boolean;
  columns?: 2 | 3 | 4;
  /** Team members (shared across languages) */
  members?: TeamMemberSettings[];
}

/**
 * Team section content (per-language)
 */
export interface TeamContent {
  title?: string;
  subtitle?: string;
  /** Per-member bio content keyed by member ID (rich HTML) */
  memberBios?: Record<string, string>;
}

/**
 * Resolved team member for rendering (combines settings + content)
 */
export interface TeamMember {
  id: string;
  name: string;
  role: string;
  imageUrl?: string;
  email?: string;
  bio?: string;
  location?: string;
  socialLinks?: SocialLink[];
}

/**
 * Stats section settings
 */
export interface StatsSettings extends BaseSectionSettings {
  layoutVariant?: StatsLayoutVariant;
  columns?: 2 | 3 | 4;
  /** Stat items (shared across languages) */
  stats?: StatSettings[];
}

/**
 * Stat item settings (shared structure, stored in settings)
 */
export interface StatSettings {
  /** Unique identifier for syncing with content texts */
  id: string;
  /** Icon identifier */
  icon?: string;
}

/**
 * Stats section content (per-language)
 */
export interface StatsContent {
  title?: string;
  subtitle?: string;
  /** Per-stat text content keyed by stat ID */
  statTexts?: Record<string, StatText>;
}

/**
 * Localized text for a stat item
 */
export interface StatText {
  value: string;
  label: string;
  description?: string;
}

/**
 * Resolved stat item for rendering (combines settings + content)
 */
export interface StatItem {
  id: string;
  value: string;
  label: string;
  description?: string;
  icon?: string;
}

/**
 * Features section settings
 */
export interface FeaturesSettings extends BaseSectionSettings {
  layoutVariant?: FeaturesLayoutVariant;
  columns?: 2 | 3 | 4;
  sectionImageId?: string;
  sectionImageUrl?: string;
}

/**
 * Features section content
 */
export interface FeaturesContent {
  title?: string;
  subtitle?: string;
  features: FeatureItem[];
}

export interface FeatureItem {
  title: string;
  description: string;
  icon?: string;
  imageUrl?: string;
}

/**
 * CTA section settings
 */
export interface CTASettings extends BaseSectionSettings {
  layoutVariant?: CTALayoutVariant;
}

/**
 * CTA section content
 */
export interface CTAContent {
  title?: string;
  subtitle?: string;
  ctaText?: string;
  ctaUrl?: string;
  secondaryCtaText?: string;
  secondaryCtaUrl?: string;
  imageUrl?: string;
}

/**
 * Blog section settings
 */
export interface BlogSettings extends BaseSectionSettings {
  layoutVariant?: BlogLayoutVariant;
  showCategory?: boolean;
  showDate?: boolean;
  showExcerpt?: boolean;
  /** Blog posts structure (shared across languages) */
  posts?: BlogPostSettings[];
}

/** Blog post shared settings (non-translatable) */
export interface BlogPostSettings {
  id: string;
  imageId: string | null;
  date: string;
  url: string;
}

/** Blog post translatable text */
export interface BlogPostText {
  title: string;
  excerpt: string;
}

/**
 * Blog section content
 */
export interface BlogContent {
  title?: string;
  subtitle?: string;
  viewAllText?: string;
  viewAllUrl?: string;
  /** New format: keyed by post ID */
  postTexts?: Record<string, BlogPostText>;
  /** @deprecated Legacy format - use postTexts instead */
  posts?: BlogPost[];
}

export interface BlogPost {
  title: string;
  excerpt?: string;
  imageUrl?: string;
  date?: string;
  category?: string;
  url?: string;
  author?: {
    name: string;
    imageUrl?: string;
    role?: string;
  };
}

/**
 * Legal page type
 */
export type LegalPageType = 'None' | 'Internal' | 'External';

/**
 * Legal page configuration for auto-generating footer links
 */
export interface LegalPageConfig {
  imprintType: LegalPageType;
  imprintExternalUrl: string | null;
  imprintContentDe?: string | null;
  imprintContentEn?: string | null;
  privacyType: LegalPageType;
  privacyExternalUrl: string | null;
  privacyContentDe?: string | null;
  privacyContentEn?: string | null;
  locale: 'de' | 'en';
}

/**
 * Theme context passed to section renderers
 */
export interface SectionTheme {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  surfaceColor: string;
  textColor: string;
  textMutedColor: string;
  linkColor: string;
  linkHoverColor: string;
  buttonTextColor: string;
  borderColor: string;
  borderRadius: string;
  fontFamily: string;
  /** Layout properties */
  buttonRadius?: string;
  shadowIntensity?: 'none' | 'sm' | 'md' | 'lg';
  containerWidth?: 'narrow' | 'default' | 'wide' | 'full';
  sectionSpacing?: 'compact' | 'default' | 'relaxed';
  backgroundPattern?: 'none' | 'dots' | 'grid' | 'waves' | 'gradient' | 'mesh' | 'noise';
}

/**
 * Default theme values used when no theme is provided.
 */
export const DEFAULT_SECTION_THEME: SectionTheme = {
  primaryColor: '#2563eb',
  secondaryColor: '#4f46e5',
  accentColor: '#06b6d4',
  backgroundColor: '#ffffff',
  surfaceColor: '#f8fafc',
  textColor: '#1e293b',
  textMutedColor: '#64748b',
  linkColor: '#2563eb',
  linkHoverColor: '#1d4ed8',
  buttonTextColor: '#ffffff',
  borderColor: '#e2e8f0',
  borderRadius: 'md',
  fontFamily: 'Inter, system-ui, sans-serif',
  buttonRadius: 'md',
  shadowIntensity: 'sm',
  containerWidth: 'default',
  sectionSpacing: 'default',
  backgroundPattern: 'none',
};

/**
 * Injection token for the centralized theme signal.
 * Provided by fullwidth-preview, injected by all section renderers.
 * This enables reactive theme updates without prop drilling.
 */
export const SECTION_THEME = new InjectionToken<WritableSignal<SectionTheme>>('SECTION_THEME');
